import { useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { uploadFile } from '@api';
import { MAX_UPLOAD_FILE_SIZE, MAX_UPLOAD_FILE_NUM } from '@constants';
import { useBehaviorData, useReviewData } from '@hooks';
import { logError } from '@log';
import { setUploadChosen } from '@store';

const FailedToastText = `Maximum upload file size: ${MAX_UPLOAD_FILE_SIZE}MB, ${MAX_UPLOAD_FILE_NUM} files at most. Please zip the files if your are uploading more than 10 images.`;

export const useUpload = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [files, setFiles] = useState([]);
  // 由于有些ClaimType有两个附件形式，所以给做了区分，最终皆会作为一个list传给后台
  const [files1, setFiles1] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastText, setToastText] = useState('');
  const [showFailedToast, setShowFailedToast] = useState(false);
  const [failedToastText, setFailedToastText] = useState('');
  // const [totalMoney, setTotalMoney] = useState(0);
  const [comments, setComments] = useState('');

  const { type, currentPolicy } = useSelector((state) => state.claim);
  const { platform } = useSelector((state) => state.policy);
  const inputValue = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAllTotalMoney } = useReviewData();
  const setReportData = useBehaviorData();
  // 当前所有选择文件的总大小（单位：Byte）
  const currentSize = files.reduce((sumSize, curFile) => sumSize + curFile.size, 0);
  const isLoading = !!files?.find((file) => file.isLoading) || !!files1.find((file) => file.isLoading);

  const totalMoney = useMemo(() => getAllTotalMoney(), []);
  const onCloseToast = () => {
    setShowToast(false);
  };

  const onCloseFailedToast = () => {
    setShowFailedToast(false);
  };

  const upload = async (fileList, originFiles) => {
    try {
      if (!fileList.length) {
        return [];
      }
      const filePathList = await uploadFile([...fileList]);
      if (!filePathList) {
        return [];
      }
      const resultFiles = [];
      filePathList.forEach((path, index) => {
        resultFiles[index] = {
          id: originFiles[index].id,
          path,
        };
      });
      return resultFiles;
    } catch (error) {
      logError(error);
      return [];
    }
  };

  const handleChange = async (event, index) => {
    const funcName = index ? setFiles1 : setFiles;
    try {
      const fileList = event.target.files;
      if (!fileList?.length) {
        return;
      }
      // 限制上传文件数量
      if (files.length + fileList.length > MAX_UPLOAD_FILE_NUM) {
        setToastText(FailedToastText);
        setShowToast(true);
        inputValue.current.value = '';
        return;
      }
      // 限制上传文件大小
      const fileSize = Array.prototype.slice.call(fileList)
        .reduce((sumSize, curFile) => sumSize + curFile.size, 0);
      if (fileSize + currentSize > MAX_UPLOAD_FILE_SIZE * 1024 * 1024) {
        setToastText(FailedToastText);
        setShowToast(true);
        inputValue.current.value = '';
        return;
      }

      setShowToast(false);
      // 添加文件到文件列表
      const newFiles = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fileList.length; i++) {
        newFiles.push({
          id: Date.now() + fileList[i].name,
          name: fileList[i].name,
          path: '',
          isLoading: true,
        });
      }
      funcName([...(index ? files1 : files), ...newFiles]);

      // 上传文件
      const resultList = await upload(fileList, newFiles);
      // 上传文件失败，从文件列表中删除刚上传的文件
      if (!resultList?.length) {
        funcName([...(index ? files1 : files)]);
        setFailedToastText(`Failed to upload ${newFiles.map((item) => item.name).join('; ')}. Please try again.`);
        setShowFailedToast(true);
        inputValue.current.value = '';
        return;
      }

      // 上传成功，更新文件列表
      funcName((preFiles) => preFiles.map((file) => {
        const resultFile = resultList.find((item) => item.id === file.id);
        if (!resultFile) {
          return { ...file };
        }
        return { ...file, path: resultFile.path, isLoading: false };
      }));

      /** 防止第二次上传相同文件失效 */
      inputValue.current.value = '';
    } catch (error) {
      console.log(error);
      setFailedToastText('upload files error');
      setShowFailedToast(true);
    }
  };

  const deleteFile = (index, num) => {
    if (num) {
      files1.splice(index, 1);
      setFiles1([...files1]);
    } else {
      files.splice(index, 1);
      setFiles([...files]);
    }
  };

  const gotoNextPage = () => {
    // console.log(`/${platform}/payment`);
    navigate(`${platform ? `/${platform}` : ''}/payment`);
  };

  // 点击 continue 按钮
  const onClickContinue = async () => {
    setReportData({ uploadTime: `${Date.now()}` });
    try {
      // if (!files?.length) {
      //   dispatch(setUploadChosen({ files: [], comments: '' }));
      //   return;
      // }
      const totalFiles = [...files, ...files1];
      setIsFetching(true);
      dispatch(setUploadChosen({ files: totalFiles, comments }));
      setIsFetching(false);
      gotoNextPage();
    } catch (error) {
      logError(error);
    }
  };
  const onCommentsChange = (e) => {
    setComments(e.target.value);
  };
  // 点击返回按钮
  const onClickBack = () => {
    dispatch(setUploadChosen({ files: [], comments: '' }));
  };

  return {
    type,
    platform,
    files,
    files1,
    inputValue,
    isLoading: Boolean(isLoading || isFetching),
    showToast,
    toastText,
    showFailedToast,
    failedToastText,
    totalMoney,
    comments,
    liabilitiesArr: currentPolicy.liabilitiesArr,
    onCommentsChange,
    onCloseFailedToast,
    handleChange,
    deleteFile,
    onClickContinue,
    onClickBack,
    onCloseToast,
  };
};
